import { defineAsyncComponent, ref } from "vue";
import { useForm } from "vee-validate";
import NykAlert from "../../NykAlert/NykAlert.vue";
import Modal from "../../Modal/Modal.vue";
import formSettings from "../settings.js";
import { useRecaptchaToken } from "../../../composables/use-recaptcha.composable";
import { GTMService } from "../../../services/gtm.service.js";

export default {
  components: {
    NykAlert,
    Modal,
  },
  props: {
    formId: {
      type: String,
    },
    formTitle: {
      type: String,
    },
    formType: {
      type: String,
      default: "custom-form",
      validator: (value) => {
        const validTypes = [
          "custom-form",
          "contact-advisor",
          "send-document-to-advisor",
        ];
        return validTypes.includes(value) || value === "";
      },
    },
    formGuid: {
      type: String,
    },
    apiPath: {
      type: String,
    },
    antiForgeryToken: {
      type: String,
    },
    formElements: {
      type: Array,
      default: () => [],
    },
    showSummarizedData: {
      type: Boolean,
    },
    summaryConfirmationButtonLabel: {
      type: String,
    },
    summaryCancellationButtonLabel: {
      type: String,
    },
    confirmationMessage: {
      type: String,
    },
    submitSuccessMessage: {
      type: String,
      default: "Formularen er blevet sendt!",
    },
    submitErrorMessage: {
      type: String,
      default: "Der opstår en fejl med insendt!",
    },
    resetAfterSubmission: {
      type: Boolean,
    },
    queryParams: {
      type: Object,
      default: () => ({}),
    },
    queryParamFields: {
      type: Array,
      default: () => [],
    },
    contactAdvisorBlockId: {
      type: String,
      required: false,
    },
  },
  setup(props) {
    const fieldPrefix = "";
    const submissionSucceeded = ref(false);
    const showStatusMessage = ref(false);
    const showConfirmationModal = ref(false);
    const confirmationData = ref([]);
    const { getRecaptchaToken } = useRecaptchaToken();
    const elements = ref(
      props.formElements.flatMap((element) => {
        const result = [];

        // Add country code element before international phone elements
        if (
          element.contentType === "CustomPhoneElementBlock" &&
          element.properties?.allowInternationalNumber
        ) {
          result.push({
            key: `${element.key}${formSettings.subFieldDelimiter}${formSettings.countryCodePostfix}`,
            properties: {
              label: element.properties.countryCodeLabel,
            },
          });
        }

        result.push(element);
        return result;
      }),
    );

    // Cache for components to avoid re-importing which causes infinite reactivity updates on the forms 'values'
    const componentCache = {};

    const getComponentType = (element) => {
      const componentMap = {
        CustomSelectElementBlock: "NykSelect",
        CustomFileUploadElementBlock: "NykInputFile",
        CustomDateTimeElementBlock: "NykDateTimePicker",
        CustomParagraphElementBlock: "NykFormParagraph",
        CustomSpacerElementBlock: "NykFormSpacer",
        CustomTitleElementBlock: "NykFormTitle",
        CustomTextboxElementBlock: "NykInput",
        CustomCprNumberElementBlock: "NykInput",
        CustomNumberElementBlock: "NykInput",
        CustomEmailElementBlock: "NykInput",
        CustomTextareaElementBlock: "NykTextarea",
        CustomAddressElementBlock: element.properties?.autoCompleteAddress
          ? "NykAddress"
          : "NykInput",
        CustomChoiceElementBlock: element.properties.allowMultiSelect
          ? "NykCheckboxGroup"
          : "NykRadioGroup",
        CustomPhoneElementBlock: "NykPhone",
        CustomCvrNumberElementBlock: "NykCvr",
        CustomSubmitButtonElementBlock: "NykSubmitButton",
        NykAdvisorSelector: "NykAdvisorSelector",
        NykCheckbox: "NykCheckbox",
      };

      const componentName = componentMap[element.contentType];

      if (!componentName) return null;

      if (!componentCache[componentName]) {
        componentCache[componentName] = defineAsyncComponent(
          () => import(`../${componentName}/${componentName}.vue`),
        );
      }

      return componentCache[componentName];
    };

    const getElementEvents = (element) => {
      if (element.contentType === "CustomCvrNumberElementBlock") {
        return {
          update: onFieldUpdate,
        };
      }
      return {};
    };

    const isRequired = (elementProperties) => {
      const validators = elementProperties.validators;

      if (validators) {
        return !!validators.find(
          (validator) => validator.type === "RequiredValidator",
        );
      }

      return undefined;
    };

    const { handleSubmit, values, meta, resetForm, setFieldValue } = useForm();

    const buildQueryParams = (formValues) => {
      const formFieldsAsQueryParams = props.queryParamFields.reduce(
        (acc, fieldName) => {
          if (formValues[fieldName]) {
            acc[fieldName] = formValues[fieldName];
          }
          return acc;
        },
        {},
      );

      return {
        ...(props.formId ? { formId: props.formId } : {}),
        ...props.queryParams,
        ...formFieldsAsQueryParams,
      };
    };

    const scrollToFirstError = () => {
      const el = document.querySelector(".nyk-form-element--validation-failed");

      if (el) {
        // Adds a fix for the element being hidden behind a fixed sticky header by adding temporary scroll margin, 
        // then scrolls to the element and removes the margin again
        el.style.scrollMarginTop = `100px`;
        el.scrollIntoView();
        el.style.scrollMarginTop = "";
      }
    };

    const onSubmit = handleSubmit(async (formValues) => {
      if (props.showSummarizedData) {
        buildConfirmationData(formValues);
        showConfirmationModal.value = true;
        return;
      }

      await submitForm(formValues);
    }, () => scrollToFirstError());

    const buildConfirmationData = (formValues) => {
      confirmationData.value = elements.value
        .filter((element) => formValues.hasOwnProperty(element.key))
        .map((element) => {
          const value = formValues[element.key];
          const keyParts = element.key.split(formSettings.subFieldDelimiter);
          const fallbackLabel = keyParts.length > 1 ? keyParts[1] : element.key;
          const elementLabel = element.properties.label || fallbackLabel;

          let displayValue = value;
          if (
            Array.isArray(value) &&
            value.length &&
            value[0] instanceof File
          ) {
            displayValue =
              value.map((f) => f.name).join(", ") || formSettings.emptyValue;
          } else if (Array.isArray(value)) {
            displayValue = value.join(", ") || formSettings.emptyValue;
          } else if (!value) {
            displayValue = formSettings.emptyValue;
          }

          return {
            label: elementLabel,
            value: displayValue,
          };
        });
    };

    const confirmSubmission = async () => {
      showConfirmationModal.value = false;
      await submitForm(
        Object.fromEntries(
          Object.entries(values).filter(
            ([key, value]) => value !== undefined && value !== "",
          ),
        ),
      );
    };

    const cancelSubmission = () => {
      showConfirmationModal.value = false;
    };

    const submitForm = async (formValues) => {
      showStatusMessage.value = false;
      submissionSucceeded.value = false;

      const formMetaData = {
        ...(props.formId ? { formId: props.formId } : {}),
        ...(props.formTitle ? { formTitle: props.formTitle } : {}),
        ...(props.formGuid ? { formGuid: props.formGuid } : {}),
        // Use origin + pathname to avoid leaking query strings or fragments that may contain PII or tokens
        referrer: window.location.origin + window.location.pathname,
        site: window.location.hostname,
      };
      try {
        const queryParamsObj = buildQueryParams(formValues);
        const urlSearchParams = new URLSearchParams(queryParamsObj);
        if (props.contactAdvisorBlockId) {
          urlSearchParams.append("blockId", props.contactAdvisorBlockId);
        }
        const finalUrl = props.apiPath + "?" + urlSearchParams.toString();

        const response = await fetch(finalUrl, {
          method: "POST",
          body: getFormData(formValues, formMetaData),
          headers: {
            RequestVerificationToken: props.antiForgeryToken,
            RecaptchaToken: await getRecaptchaToken(),
          },
        });

        if (response.status === 200) {
          submissionSucceeded.value = true;

          // Push form submission event to GTM data layer
          GTMService.pushToDataLayer([
            {
              event: "formSubmitted",
              elementName: props.formTitle || props.formId || "unknown-form",
              form_type: props.formType,
              formId: props.formId || "unknown-form-id",
              formGuid: props.formGuid || "unknown-form-guid",
            },
          ]);
        } else {
          submissionSucceeded.value = false;
        }
      } catch (error) {
        submissionSucceeded.value = false;
        console.error("Error submitting form:", error);
      } finally {
        if (props.resetAfterSubmission) {
          resetForm();
        }

        showStatusMessage.value = true;
      }
    };

    const appendFiles = (formData, files, element) => {
      const fieldName = element.contentType === "CustomFileUploadElementBlock" &&
        element.properties?.prefixFileNameWithFieldName
        ? element.displayName?.trim().replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")
        : "";

      return files.map((file) => {
        const fileName = fieldName ? `${fieldName}_${file.name}` : file.name;
        formData.append("file", file, fileName);
        return fileName;
      });
    };

    const getFormData = (formValues, formMetaData) => {
      const formData = new FormData();
      let hasFiles = false;

      const formularData = elements.value
        .filter((element) => formValues.hasOwnProperty(element.key))
        .reduce((data, element) => {
          const name = element.key;
          const finalValuePrefix = element.properties?.finalValuePrefix;
          let value = formValues[name];
          const normalizedName = name.replace(fieldPrefix, "");
          let valueType = Array.isArray(value) ? "array" : typeof value;

          if (element && element.contentType === "CustomDateTimeElementBlock") {
            valueType = "date";
          }

          if (
            valueType === "array" &&
            value.length !== 0 &&
            value[0] instanceof File
          ) {
            value = appendFiles(formData, value, element);
            hasFiles = true;
          }

          // Normalize CPR and phone number field values for Salesforce matching.
          // CPR fields: strip the CPR delimiter (e.g. "DDMMYY-0000" -> "DDMMYY0000").
          // Phone fields: strip whitespace (e.g. "00 00 00 00" -> "00000000").
          if (element && typeof value === "string") {
            if (element.contentType === "CustomCprNumberElementBlock") {
              value = value.replace(
                new RegExp(formSettings.cprDelimiter, "g"),
                "",
              );
            } else if (element.contentType === "CustomPhoneElementBlock") {
              value = value.replace(
                new RegExp(formSettings.phoneDelimiter, "g"),
                "",
              );
            }
          }

          return [
            ...data,
            {
              name,
              name: element ? element.displayName : normalizedName,
              label: element ? element.properties.label : "",
              value: ["object", "array", "boolean", "number"].includes(
                valueType,
              )
                ? JSON.stringify(value)
                : finalValuePrefix
                  ? `${finalValuePrefix}${value}`
                  : value,
              type: valueType,
            },
          ];
        }, []);

      const fullData = {
        ...formMetaData,
        hasFiles,
        formularData: formularData,
      };

      formData.append("formData", JSON.stringify(fullData));
      return formData;
    };

    const checkVisibilityCondition = (condition) => {
      const comparingFieldName = `${fieldPrefix}${condition.field}`;
      const valueToCompareAgainst = condition.fieldValue;

      if (condition.operator === "Equals") {
        return values[comparingFieldName] === valueToCompareAgainst;
      }

      if (condition.operator === "NotEquals") {
        return values[comparingFieldName] !== valueToCompareAgainst;
      }

      if (condition.operator === "Contains") {
        return values[comparingFieldName]?.includes(valueToCompareAgainst);
      }

      if (condition.operator === "NotContains") {
        return !values[comparingFieldName]?.includes(valueToCompareAgainst);
      }

      if (condition.operator === "MatchRegularExpression") {
        const regex = new RegExp(valueToCompareAgainst);
        return regex.test(values[comparingFieldName]);
      }

      return true;
    };

    const isVisible = (element) => {
      if (!element.properties.conditions) return true;

      const conditionsSatisfied =
        element.properties.conditionCombination === "Any"
          ? element.properties.conditions.some(checkVisibilityCondition)
          : element.properties.conditions.every(checkVisibilityCondition);

      if (
        element.properties.satisfiedAction &&
        element.properties.satisfiedAction === "hide"
      ) {
        return !conditionsSatisfied;
      }

      return conditionsSatisfied;
    };

    const extraElementSpecificProps = (element, rest) => {
      if (element.contentType === "CustomNumberElementBlock") {
        return {
          format: {
            mask: Number,
            normalizeZeros: false,
            min: 0,
            max: Number.MAX_VALUE,
            ...(element.properties.noThousandSeparator
              ? {}
              : { thousandsSeparator: "." }),
            ...(element.properties.allowDecimal
              ? {
                  scale: 2,
                  radix: ",",
                  mapToRadix: ["."],
                }
              : {
                  scale: 0,
                }),
          },
          inputMode: "numeric",
          ...rest,
        };
      } else if (element.contentType === "CustomCvrNumberElementBlock") {
        const elementProperties = Object.entries(rest);

        const relatedFields = elementProperties
          .filter(([field]) => !formSettings.primitiveProps.includes(field))
          .reduce(
            (fields, [field, value]) => ({ ...fields, [field]: value }),
            {},
          );

        rest = elementProperties
          .filter(([field]) => formSettings.primitiveProps.includes(field))
          .reduce(
            (fields, [field, value]) => ({ ...fields, [`${field}`]: value }),
            {},
          );

        const allowConstructedCvr = !!element.properties.allowConstructedCvrNumber;

        return {
          relatedFields,
          ...rest,
          validators: rest.validators
            ? rest.validators.map((v) =>
                v.type === "CvrValidator"
                    ? { ...v, model: { ...v.model, allowConstructedCvr } }
                    : v,
            )
            : rest.validators,
        };
      } else if (element.contentType === "CustomCprNumberElementBlock") {
        const cprAgeValidator = element.properties.validators
          ? element.properties.validators.find(
              (v) => v.type === "CprAgeValidator",
            )
          : null;

        return {
          ...rest,
          format: formSettings.formats.cpr,
          inputMode: "numeric",
          validators: [
            ...(element.properties.validators
              ? element.properties.validators.filter(
                  (v) => v.type !== "CprAgeValidator",
                )
              : []),
            ...(cprAgeValidator
              ? [
                  {
                    type: "CprAgeValidator",
                    model: {
                      message: element.properties.invalidCprFormatMessage,
                      minAge: element.properties.minAge,
                      maxAge: element.properties.maxAge,
                      tooYoungMessage: element.properties.tooYoungMessage,
                      tooOldMessage: element.properties.tooOldMessage,
                    },
                  },
                ]
              : []),
          ],
        };
      } else if (element.contentType === "CustomEmailElementBlock") {
        return {
          ...rest,
          type: "email",
          validators: [
            ...element.properties.validators,
            {
              type: "EmailFormatValidator",
              model: {
                message: element.properties.invalidEmailFormatErrorMessage,
              },
            },
            ...(element.properties.validDomains &&
            element.properties.validDomains.length > 0
              ? [
                  {
                    type: "EmailDomainValidator",
                    model: {
                      message: element.properties.invalidDomainErrorMessage,
                      validDomains: element.properties.validDomains || [],
                    },
                  },
                ]
              : []),
          ],
        };
      }

      return { ...rest };
    };

    const getStaticElementProps = () => {
      return props.formElements.reduce((map, element) => {
        const { type, ...rest } = element.properties;

        return {
          ...map,
          [element.key]: {
            name: `${fieldPrefix + element.key}`,
            required: isRequired(element.properties),
            ...extraElementSpecificProps(element, rest),
          },
        };
      }, {});
    };

    const onFieldUpdate = (updates) => {
      Object.entries(updates).forEach(([fieldName, value]) => {
        const element = props.formElements.find(
          (el) => el.displayName === fieldName,
        );
        if (element && value) {
          setFieldValue(element.key, value);
        }
      });
    };

    return {
      submissionSucceeded,
      showStatusMessage,
      showConfirmationModal,
      confirmationData,
      values,
      staticElementProps: ref(getStaticElementProps()),
      isVisible,
      onSubmit,
      confirmSubmission,
      cancelSubmission,
      getComponentType,
      getElementEvents,
      onFieldUpdate,
    };
  },
};
